import api from './api';
import { setUser, getUserLanguage, clearUser, getUser } from '../utils/authUtils';

export interface RegisterCredentials {
    email: string;
    password1: string;
    password2: string;
}

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface PasswordResetConfirm {
    uidb64: string;
    token: string;
    new_password: string;
}

export interface AIToken {
    name?: string;
    provider?: string;
    api_key?: string;
    is_active?: boolean;
}

export interface ProfileData {
    name?: string;
    age?: number;
    sex?: string;
    socialLinks?: { 
        twitter?: string; 
        facebook?: string; 
        linkedin?: string; 
    };
    preferences?: { 
        theme?: string; 
        language?: string; 
    };
}

export interface GoogleAuth {
    sub: string;               // Google user ID (unique)
    email: string;
    email_verified: boolean;
    name?: string;
    given_name?: string;
    family_name?: string;
    picture?: string;
    id_token: string;          // raw token for backend to verify
}

export const authService = {
    /**
     * Проверяет, авторизован ли пользователь
     * @returns {boolean} true если пользователь авторизован
     */
    isAuthenticated: (): boolean => {
        const user = getUser();
        return !!user;
    },

    /**
     * Регистрация нового пользователя
     * @param {RegisterCredentials} credentials - данные для регистрации
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    register: async (credentials: RegisterCredentials) => {
        try {
            const data = { ...credentials };
            const response = await api.post('/auth/register/', data);
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'REGISTRATION_FAILED';
            console.error('Registration error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Авторизация пользователя
     * @param {LoginCredentials} credentials - данные для входа
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    login: async (credentials: LoginCredentials) => {
        try {
            const data = { ...credentials };
            const response = await api.post('/auth/login/', data);
            const { user } = response.data;
            
            if (user) {
                setUser(user);
            }
            
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'LOGIN_FAILED';
            console.error('Login error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Выход из системы
     * @returns {Promise<void>}
     */
    logout: async (): Promise<void> => {
        try {
            // Пытаемся уведомить сервер о выходе
            await api.post('/auth/logout/');
        } catch (error: any) {
            // Игнорируем ошибки сервера при logout - главное очистить локальные данные
            console.warn('Server logout failed, but proceeding with local cleanup:', error);
        } finally {
            // Всегда очищаем локальные данные пользователя
            clearUser();
        }
    },

    /**
     * Подтверждение email пользователя
     * @param {string} uidb64 - закодированный ID пользователя
     * @param {string} token - токен подтверждения
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    verifyEmail: async (uidb64: string, token: string) => {
        try {
            const response = await api.post(`/auth/verify-email/${uidb64}/${token}/`);
            const { user } = response.data;
            
            if (user) {
                setUser(user);
            }
            
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'EMAIL_VERIFICATION_FAILED';
            console.error('Email verification error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Повторная отправка письма подтверждения
     * @param {string} email - email пользователя
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    resendVerification: async (email: string) => {
        try {
            const response = await api.post('/auth/resend-verification/', 
                { email: email }, 
                { params: { language: getUserLanguage() } }
            );
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'RESEND_VERIFICATION_FAILED';
            console.error('Resend verification error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Запрос на сброс пароля
     * @param {string} email - email пользователя
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    passwordResetRequest: async (email: string) => {
        try {
            const response = await api.post('/auth/password-reset/', 
                { email: email }, 
                { params: { language: getUserLanguage() } }
            );
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'PASSWORD_RESET_REQUEST_FAILED';
            console.error('Password reset request error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Подтверждение сброса пароля
     * @param {PasswordResetConfirm} data - данные для сброса пароля
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    passwordResetConfirm: async (data: PasswordResetConfirm) => {
        try {
            const response = await api.post('/auth/password-reset/confirm/', data);
            const { user } = response.data;
            
            if (user) {
                setUser(user);
            }
            
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'PASSWORD_RESET_CONFIRM_FAILED';
            console.error('Password reset confirm error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Создание AI токена
     * @param {AIToken} data - данные для создания токена
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    createAIToken: async (data: AIToken) => {
        try {
            const response = await api.post('/auth/ai-token/', data);
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'CREATE_AI_TOKEN_FAILED';
            console.error('Create AI token error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Удаление AI токена
     * @param {string} tokenId - ID токена для удаления
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    deleteAIToken: async (tokenId: string) => {
        try {
            const response = await api.delete(`/auth/ai-token/${tokenId}/`);
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'DELETE_AI_TOKEN_FAILED';
            console.error('Delete AI token error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Получение всех AI токенов пользователя
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    getAITokens: async () => {
        try {
            const response = await api.get('/auth/ai-token/');
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'GET_AI_TOKENS_FAILED';
            console.error('Get AI tokens error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Получение конкретного AI токена
     * @param {string} tokenId - ID токена
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    getAIToken: async (tokenId: string) => {
        try {
            const response = await api.get(`/auth/ai-token/${tokenId}/`);
            const { user } = response.data;
            
            if (user) {
                setUser(user);
            }
            
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'GET_AI_TOKEN_FAILED';
            console.error('Get AI token error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Обновление AI токена
     * @param {string} tokenId - ID токена
     * @param {AIToken} data - данные для обновления
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    updateAIToken: async (tokenId: string, data: AIToken) => {
        try {
            const response = await api.put(`/auth/ai-token/${tokenId}/`, data);
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'UPDATE_AI_TOKEN_FAILED';
            console.error('Update AI token error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Авторизация через Google
     * @param {GoogleAuth} data - данные от Google
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    googleAuth: async (data: GoogleAuth) => {
        try {
            const response = await api.post('/auth/google/', data);
            const { user } = response.data;
            
            if (user) {
                setUser(user);
            }
            
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'GOOGLE_AUTH_FAILED';
            console.error('Google auth error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Авторизация через Facebook
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    facebookAuth: async () => {
        try {
            const response = await api.post('/auth/facebook/');
            const { user } = response.data;
            
            if (user) {
                setUser(user);
            }
            
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'FACEBOOK_AUTH_FAILED';
            console.error('Facebook auth error:', error);
            return { success: false, message_code: errorCode };
        }
    },
    
    /**
     * Получение профиля пользователя
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    getProfile: async () => {
        try {
            const response = await api.get('/auth/profile/');
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'GET_PROFILE_FAILED';
            console.error('Get profile error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Обновление профиля пользователя
     * @param {ProfileData} profileData - данные профиля для обновления
     * @returns {Promise<{success: boolean, data?: any, message_code?: string}>}
     */
    updateProfile: async (profileData: ProfileData) => {
        try {
            const response = await api.put('/auth/profile/', profileData);
            const { user } = response.data;
            
            if (user) {
                setUser(user);
            }
            
            return { success: true, data: response.data };
        } catch (error: any) {
            const errorCode = error.response?.data?.message_code || error.response?.data?.code || 'UPDATE_PROFILE_FAILED';
            console.error('Update profile error:', error);
            return { success: false, message_code: errorCode };
        }
    },

    /**
     * Обновление токена доступа
     * @returns {Promise<{success: boolean, data?: any}>}
     */
    refreshToken: async () => {
        try {
            const response = await api.post('/auth/token/refresh/');
            return { success: true, data: response.data };
        } catch (error: any) {
            console.error('Token refresh error:', error);
            return { success: false, data: null };
        }
    },

    /**
     * Получение текущих данных пользователя из localStorage
     * @returns {any|null} данные пользователя или null
     */
    getCurrentUser: () => {
        return getUser();
    },

    /**
     * Проверка валидности токена
     * @returns {Promise<boolean>} true если токен валиден
     */
    validateToken: async (): Promise<boolean> => {
        try {
            await api.get('/auth/me/');
            return true;
        } catch (error) {
            return false;
        }
    },

    /**
     * Очистка всех данных пользователя
     * @returns {void}
     */
    clearUserData: (): void => {
        clearUser();
    }
};